import {
	FILTER_CONTROL_HEIGHT_CLASS,
	FilterBar,
	FilterField,
	Input,
	PageHeader,
	PaginationPageDefault,
} from "@components";
import { Button } from "@components/button/button-untitledui";
import { usePaginationTotal } from "@components/pagination/use-pagination-total";
import { Plus, SearchLg } from "@untitledui/icons";
import { useCallback, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmModal } from "@/components/application/modals/confirm-modal";
import { useClipboard } from "@/hooks/use-clipboard";
import { useToast } from "@/providers/toast-provider";
import {
	checklist_formulario_visualizar,
	formularioCompartilharLink,
	formulariosListaBuscar,
} from "@/services/analytics/events";
import { useDebouncedCommit } from "@/shared/hooks/use-debounced-commit";
import { cx } from "@/utils/cx";
import {
	FormStatusTabs,
	FormsListTable,
	FormViewModal,
} from "../../components";
import { useArchiveChecklistTemplate } from "../../hooks/useChecklistTemplateMutations";
import { useChecklistTemplatesList } from "../../hooks/useChecklistTemplateQueries";
import type { PaginatedChecklistTemplateItem } from "../../types";
import { TemplateStatus } from "../../types/template";

const ITEMS_PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 500;

const EMPTY_STATUS_COUNTS = {
	active: 0,
	draft: 0,
	archived: 0,
};

type ViewModalMode = "view" | "duplicate";

const FormList = () => {
	const navigate = useNavigate();
	const [currentPage, setCurrentPage] = useState(1);
	const [searchInput, setSearchInput] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [statusTab, setStatusTab] = useState<
		(typeof TemplateStatus)[keyof typeof TemplateStatus]
	>(TemplateStatus.Active);
	const [viewModalTemplateId, setViewModalTemplateId] = useState<number | null>(
		null
	);
	const [viewModalMode, setViewModalMode] = useState<ViewModalMode>("view");
	const [archiveTemplateId, setArchiveTemplateId] = useState<number | null>(
		null
	);
	const { copy } = useClipboard();
	const { showToast } = useToast();
	const archiveTemplate = useArchiveChecklistTemplate();

	useDebouncedCommit({
		value: searchInput,
		applied: searchTerm,
		delay: SEARCH_DEBOUNCE_MS,
		onCommit: (term) => {
			setSearchTerm(term);
			setCurrentPage(1);
			if (term) {
				formulariosListaBuscar(term);
			}
		},
	});

	const searchInputId = useId();
	const templatesQuery = useChecklistTemplatesList({
		PageNumber: currentPage,
		PageSize: ITEMS_PER_PAGE,
		Name: searchTerm || undefined,
		Status: statusTab,
	});
	const {
		data: templatesResponse,
		isFetching,
		isError,
		refetch,
	} = templatesQuery;

	const templates = templatesResponse?.templates;
	const statusCounts = templatesResponse?.statusCounts ?? EMPTY_STATUS_COUNTS;
	const { total: totalPages } = usePaginationTotal({
		page: currentPage,
		onPageChange: setCurrentPage,
		criteria: {
			name: searchTerm || undefined,
			status: statusTab,
			pageSize: ITEMS_PER_PAGE,
		},
		totalPages: templates?.totalPages,
		query: templatesQuery,
	});

	const openViewModal = useCallback(
		(item: PaginatedChecklistTemplateItem, mode: ViewModalMode) => {
			if (!item?.id) {
				return;
			}
			checklist_formulario_visualizar(item.id);
			setViewModalMode(mode);
			setViewModalTemplateId(item.id);
		},
		[]
	);

	const closeViewModal = () => {
		setViewModalTemplateId(null);
		setViewModalMode("view");
	};

	const handleConfirmDuplicate = () => {
		if (!viewModalTemplateId) {
			return;
		}
		const templateId = viewModalTemplateId;
		closeViewModal();
		navigate(`/forms/new?duplicateFrom=${templateId}`);
	};

	const handleArchiveFromViewModal = () => {
		if (!viewModalTemplateId) {
			return;
		}
		const templateId = viewModalTemplateId;
		closeViewModal();
		setArchiveTemplateId(templateId);
	};

	const handleCopyLink = async (item: PaginatedChecklistTemplateItem) => {
		if (!item.checklistUrl) {
			return;
		}
		await copy(item.checklistUrl);
		formularioCompartilharLink(item.checklistUrl);
		showToast({
			variant: "success",
			title: "Link copiado!",
		});
	};

	const handleConfirmArchive = async () => {
		if (!archiveTemplateId) {
			return;
		}

		try {
			await archiveTemplate.mutateAsync(archiveTemplateId);
			setArchiveTemplateId(null);
		} catch {
			setArchiveTemplateId(null);
		}
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		if (value.length > 80) {
			return;
		}
		setSearchInput(value);
	};

	const canDuplicateFromModal = statusTab !== TemplateStatus.Archived;
	const canArchiveFromModal =
		statusTab === TemplateStatus.Active || statusTab === TemplateStatus.Draft;

	return (
		<>
			<FormViewModal
				canArchive={canArchiveFromModal}
				canDuplicate={canDuplicateFromModal}
				isOpen={Boolean(viewModalTemplateId)}
				mode={viewModalMode}
				onArchive={handleArchiveFromViewModal}
				onClose={closeViewModal}
				onDuplicate={handleConfirmDuplicate}
				templateId={viewModalTemplateId}
			/>

			<ConfirmModal
				cancelLabel="Cancelar"
				confirmLabel="Confirmar"
				description="Ao clicar em Confirmar, o formulário será arquivado permanentemente."
				isConfirmLoading={archiveTemplate.isPending}
				isOpen={Boolean(archiveTemplateId)}
				onClose={() => setArchiveTemplateId(null)}
				onConfirm={handleConfirmArchive}
				title="Confirmar arquivamento de Formulário?"
			/>

			<div
				className="flex w-full flex-col gap-6 px-6 py-8"
				data-slot="forms-list"
			>
				<PageHeader title="Lista de formulários" />

				<FilterBar
					actions={
						<Button
							color="primary"
							iconLeading={Plus}
							onClick={() => {
								navigate("/forms/new");
							}}
							size="sm"
						>
							Novo formulário
						</Button>
					}
					className="mb-0"
					label="Filtros de formulários"
				>
					<FilterField
						className="sm:max-w-[600px]"
						grow
						htmlFor={searchInputId}
						label="Pesquise pelo formulário"
						labelVisibility="hidden"
					>
						<div className="relative w-full">
							<SearchLg
								aria-hidden
								className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3 size-4 text-fg-quaternary"
							/>
							<Input
								className={cx(FILTER_CONTROL_HEIGHT_CLASS, "pl-9")}
								id={searchInputId}
								maxLength={80}
								onChange={handleSearchChange}
								placeholder="Pesquise pelo formulário..."
								value={searchInput}
							/>
						</div>
					</FilterField>
				</FilterBar>

				<FormStatusTabs
					activeTab={statusTab}
					counts={statusCounts}
					onTabChange={(tab) => {
						setStatusTab(tab);
						setCurrentPage(1);
					}}
				/>

				<FormsListTable
					footer={
						totalPages !== null && totalPages > 1 ? (
							<PaginationPageDefault
								className="p-5"
								onPageChange={(page) => {
									setCurrentPage(page);
								}}
								page={currentPage}
								total={totalPages}
							/>
						) : null
					}
					isError={isError}
					isFetching={isFetching}
					items={templates?.items ?? []}
					onArchive={(item) => {
						if (item?.id) {
							setArchiveTemplateId(item.id);
						}
					}}
					onCopyLink={handleCopyLink}
					onDuplicate={(item) => {
						openViewModal(item, "duplicate");
					}}
					onEdit={(item) => {
						if (item?.id) {
							navigate(`/forms/new?editFrom=${item.id}`);
						}
					}}
					onRetry={() => {
						refetch();
					}}
					onRowClick={(item) => {
						openViewModal(item, "view");
					}}
					statusTab={statusTab}
				/>
			</div>
		</>
	);
};

export default FormList;
